import "./Search.css";
import { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const tabs = ["all", "players", "teams", "competitions", "news", "matches"];

const Search = ({
  dark,
  searchValue,
  setSearchValue,
  MyBaseUrl,
  Teams = [],
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const tabsRef = useRef(null);

  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(false);

  const [results, setResults] = useState({
    players: [],
    teams: [],
    competitions: [],
    news: [],
    matches: [],
  });

  useEffect(() => {
    const q = new URLSearchParams(location.search).get("q") || "";
    setSearchValue(q);
  }, [location.search, setSearchValue]);

  useEffect(() => {
    if (!searchValue?.trim()) {
      setResults({
        players: [],
        teams: [],
        competitions: [],
        news: [],
        matches: [],
      });
      return;
    }

    const fetchSearch = async () => {
      try {
        setLoading(true);

        const res = await fetch(
          `${MyBaseUrl}/search?q=${encodeURIComponent(searchValue)}`,
        );

        const data = await res.json();

        const searchData = data?.data || [];

        const formattedResults = {
          players: searchData.filter(
            (item) => item.type === "player" || item.type === "players",
          ),

          teams: searchData.filter(
            (item) => item.type === "team" || item.type === "teams",
          ),

          competitions: searchData.filter(
            (item) =>
              item.type === "competition" || item.type === "competitions",
          ),

          news: searchData.filter(
            (item) => item.type === "post" || item.type === "news",
          ),

          matches: searchData.filter(
            (item) => item.type === "match" || item.type === "matches",
          ),
        };

        setResults(formattedResults);
      } catch (err) {
        console.log(err);

        setResults({
          players: [],
          teams: [],
          competitions: [],
          news: [],
          matches: [],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSearch();
  }, [searchValue]);

  const getFiltered = () => {
    if (activeTab === "all") {
      return [
        ...results.players,
        ...results.teams,
        ...results.competitions,
        ...results.news,
        ...results.matches,
      ];
    }

    return results[activeTab] || [];
  };

  return (
    <>
      <Helmet>
        <title>
          {searchValue?.trim()
            ? `${searchValue} Search Results | Players, Teams, Matches & News | FadaSports`
            : "Search Football Players, Teams, Matches & News | FadaSports"}
        </title>

        <meta
          name="description"
          content={
            searchValue?.trim()
              ? `Explore search results for ${searchValue} on FadaSports. Find football players, clubs, competitions, live matches, transfer news, scores, statistics and breaking sports updates.`
              : "Search football players, clubs, competitions, live matches, scores, transfer news and sports updates on FadaSports."
          }
        />

        <meta
          name="keywords"
          content={
            searchValue?.trim()
              ? `${searchValue}, ${searchValue} news, ${searchValue} stats, football search, player search, team search, match search, sports news, live scores, FadaSports`
              : "football search, sports search, player stats, football teams, live scores, football news, transfer updates, FadaSports"
          }
        />

        <meta name="robots" content="index, follow, max-image-preview:large" />

        <meta name="author" content="FadaSports" />

        <meta name="theme-color" content="#000000" />

        <link
          rel="canonical"
          href={
            searchValue?.trim()
              ? `https://fadasports.com/search?q=${encodeURIComponent(searchValue)}`
              : "https://fadasports.com/search"
          }
        />

        <meta property="og:type" content="website" />

        <meta property="og:site_name" content="FadaSports" />

        <meta
          property="og:url"
          content={
            searchValue?.trim()
              ? `https://fadasports.com/search?q=${encodeURIComponent(searchValue)}`
              : "https://fadasports.com/search"
          }
        />

        <meta
          property="og:title"
          content={
            searchValue?.trim()
              ? `${searchValue} Search Results | FadaSports`
              : "Search Football Content | FadaSports"
          }
        />

        <meta
          property="og:description"
          content={
            searchValue?.trim()
              ? `Discover players, clubs, football matches, transfer stories and sports news related to ${searchValue} on FadaSports.`
              : "Search football players, teams, competitions, live matches and sports news on FadaSports."
          }
        />

        <meta
          property="og:image"
          content="https://fadasports.com/og-image.png"
        />

        <meta property="og:image:alt" content="FadaSports football search" />

        <meta property="og:locale" content="en_US" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content={
            searchValue?.trim()
              ? `${searchValue} Search Results | FadaSports`
              : "Football Search | FadaSports"
          }
        />

        <meta
          name="twitter:description"
          content={
            searchValue?.trim()
              ? `Search results for ${searchValue} including football players, teams, matches, scores and breaking news.`
              : "Search football players, clubs, live scores and sports news on FadaSports."
          }
        />

        <meta
          name="twitter:image"
          content="https://fadasports.com/og-image.png"
        />

        <meta name="twitter:site" content="@FadaSports" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SearchResultsPage",

            name: searchValue?.trim()
              ? `${searchValue} Search Results`
              : "Football Search",

            url: searchValue?.trim()
              ? `https://fadasports.com/search?q=${encodeURIComponent(searchValue)}`
              : "https://fadasports.com/search",

            description: searchValue?.trim()
              ? `Search results for ${searchValue} on FadaSports including football players, clubs, competitions, matches and sports news.`
              : "Search football players, clubs, competitions and sports news on FadaSports.",

            publisher: {
              "@type": "Organization",
              name: "FadaSports",
              logo: {
                "@type": "ImageObject",
                url: "https://fadasports.com/og-image.png",
              },
            },

            inLanguage: "en-US",
          })}
        </script>
      </Helmet>

      <div className="search-page">
        <div className={`thesearchhead ${dark ? "dark" : "light"}`}>
          <div className="search-header">
            <input
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  navigate(`/search?q=${encodeURIComponent(searchValue)}`);
                }
              }}
              placeholder="Search players, teams, competitions..."
              style={
                dark
                  ? {
                      backgroundColor: "#0c0c0c3f",
                      color: "white",
                    }
                  : {}
              }
            />
          </div>

          <div
            ref={tabsRef}
            className="tabs"
            style={dark ? { backgroundColor: "#1b1b1bb0" } : {}}>
            {tabs.map((tab) => (
              <span
                key={tab}
                className={activeTab === tab ? "active" : ""}
                onClick={(e) => {
                  setActiveTab(tab);

                  e.target.scrollIntoView({
                    behavior: "smooth",
                    inline: "start",
                    block: "nearest",
                  });
                }}
                style={
                  dark
                    ? {
                        color: "#fafafaff",
                      }
                    : {
                        color: activeTab === tab ? "#fafafaff" : "",
                      }
                }>
                {tab}
              </span>
            ))}
          </div>
        </div>

        <div className="results">
          {loading ? (
            <p style={dark ? { color: "#fafafaff" } : { color: "#3e3f3fe0" }}>
              Searching...
            </p>
          ) : getFiltered().length === 0 && searchValue !== "" ? (
            <p style={dark ? { color: "#fafafaff" } : { color: "#3e3f3fe0" }}>
              No results found
            </p>
          ) : (
            getFiltered().map((item, i) => (
              <>
                {item.type === "team" && (
                  <Link
                    to={`/teams/${item.title
                      .toLowerCase()
                      .replace(/\./g, "")
                      .replace(/\s+/g, "")
                      .replace(/[^a-z0-9-]/g, "")
                      .replace(
                        /-+/g,
                        "",
                      )}-${Teams?.find((t) => t.name?.toLowerCase().trim() === item.title?.toLowerCase().trim())?.api_id || item?.id}`}
                    state={{
                      searchTeam: item,
                      matchedTeam: Teams?.find(
                        (t) =>
                          t.name?.toLowerCase().trim() ===
                          item.title?.toLowerCase().trim(),
                      ),
                    }}>
                    <div
                      key={`${item.type}-${item.id}-${i}`}
                      className="result-card"
                      style={dark ? { backgroundColor: "#0c0c0c3f" } : {}}>
                      <img
                        src={
                          item.image ||
                          "https://via.placeholder.com/80x80?text=No+Image"
                        }
                        alt=""
                      />

                      <div style={{ margin: "0 0 0 0px" }}>
                        <h4
                          style={
                            dark ? { color: "#fafafaff" } : { color: "#161819" }
                          }>
                          {item.title}
                        </h4>
                        <small
                          style={
                            dark
                              ? { color: "#fafafaff", opacity: "0.8" }
                              : { color: "#161819", opacity: "0.8" }
                          }>
                          {item.country}
                        </small>
                      </div>
                    </div>
                  </Link>
                )}

                {item.type === "competition" && (
                  <Link
                    to={`/competition/${item.title
                      .toLowerCase()
                      .replace(/\./g, "")
                      .replace(/\s+/g, "-")
                      .replace(/[^a-z0-9-]/g, "")
                      .replace(/-+/g, "-")}/${item.id}`}>
                    <div
                      key={`${item.type}-${item.id}-${i}`}
                      className="result-card"
                      style={dark ? { backgroundColor: "#0c0c0c3f" } : {}}>
                      <img
                        src={
                          item.image ||
                          "https://via.placeholder.com/80x80?text=No+Image"
                        }
                        alt=""
                      />

                      <div style={{ margin: "0 0 0 0px" }}>
                        <h4
                          style={
                            dark ? { color: "#fafafaff" } : { color: "#161819" }
                          }>
                          {item.title}
                        </h4>
                      </div>
                    </div>
                  </Link>
                )}

                {item.type === "post" && (
                  <Link to={`/news/${item.slug}`}>
                    <div
                      key={`${item.type}-${item.id}-${i}`}
                      className="result-card"
                      style={dark ? { backgroundColor: "#0c0c0c3f" } : {}}>
                      <img
                        src={
                          item.image ||
                          "https://via.placeholder.com/80x80?text=No+Image"
                        }
                        alt=""
                        style={{ objectFit: "cover" }}
                      />

                      <div>
                        <h4
                          style={
                            dark ? { color: "#fafafaff" } : { color: "#161819" }
                          }>
                          {item.title}
                        </h4>
                      </div>
                    </div>
                  </Link>
                )}

                {item.type === "player" && (
                  <Link
                    to={`/players/${item.title
                      .toLowerCase()
                      .replace(/\s+/g, "-")
                      .replace(/[^a-z0-9.-]/g, "")
                      .replace(/-+/g, "-")}-${item?.id}`}>
                    <div
                      key={`${item.type}-${item.id}-${i}`}
                      className="result-card"
                      style={dark ? { backgroundColor: "#0c0c0c3f" } : {}}>
                      <img
                        src={
                          item.image ||
                          "https://via.placeholder.com/80x80?text=No+Image"
                        }
                        alt=""
                      />

                      <div>
                        <h4
                          style={
                            dark ? { color: "#fafafaff" } : { color: "#161819" }
                          }>
                          {item.title}
                        </h4>

                        <p
                          style={
                            dark
                              ? { color: "#fafafaff", opacity: "0.8" }
                              : { color: "#161819", opacity: "0.8" }
                          }>
                          {item.nationality}
                        </p>
                        <small
                          style={
                            dark
                              ? { color: "#fafafaff", opacity: "0.8" }
                              : { color: "#161819", opacity: "0.8" }
                          }>
                          {item.position}
                        </small>
                      </div>
                    </div>
                  </Link>
                )}

                {item.type === "match" && (
                  <Link
                    to={`/matches/${`${item.home_team?.name}-${item.away_team?.name}`
                      .toLowerCase()
                      .replace(/\s+/g, "-")
                      .replace(/[^a-z0-9-]/g, "")
                      .replace(/-+/g, "-")}/${item.id}`}>
                    <div
                      key={`${item.type}-${item.id}-${i}`}
                      className="matchtable"
                      style={dark ? { backgroundColor: "#0c0c0c3f" } : {}}>
                      <span
                        className="myspan"
                        style={
                          dark ? { color: "#dbdbdbff" } : { color: "#161819" }
                        }>
                        <h3
                          className={
                            item.status === "1H" ||
                            item.status === "2H" ||
                            item.status === "LIVE"
                              ? "live-time"
                              : ""
                          }
                          style={
                            item.status === "1H" ||
                            item.status === "2H" ||
                            item.status === "LIVE"
                              ? {}
                              : dark
                                ? { color: "#dfdfdfff" }
                                : { color: "#3e3f3f" }
                          }>
                          {item.status === "FT"
                            ? "FT"
                            : item.status === "NS"
                              ? new Date(item.date).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false,
                                })
                              : // : item.status === "1H"
                                // ? "1H"
                                // : item.status === "2H"
                                // ? "2H"
                                item.status === "HT"
                                ? "HT"
                                : item.status === "ET"
                                  ? "Extra"
                                  : item.status === "PEN"
                                    ? "PK"
                                    : item.status}
                        </h3>
                      </span>

                      <span
                        className="matchspam"
                        style={{ backgroundColor: "transparent" }}>
                        <nav style={{ backgroundColor: "transparent" }}>
                          <wrap className="MM">
                            <img
                              src={item.home_team?.logo}
                              width="25"
                              alt={item.home_team?.name}
                            />

                            <p
                              style={
                                dark
                                  ? { color: "#dbdbdbff" }
                                  : { color: "#161819" }
                              }>
                              {item.home_team?.name}
                            </p>
                          </wrap>

                          <wrap className="NN">
                            <strong
                              style={
                                dark
                                  ? { color: "#dbdbdbff" }
                                  : { color: "#161819" }
                              }>
                              {item.score ? item.score.split("-")[0] : ""}
                            </strong>
                          </wrap>
                        </nav>

                        <nav style={{ backgroundColor: "transparent" }}>
                          <wrap className="MM">
                            <img
                              src={item.away_team?.logo}
                              width="25"
                              alt={item.away_team?.name}
                            />

                            <p
                              style={
                                dark
                                  ? { color: "#dbdbdbff" }
                                  : { color: "#161819" }
                              }>
                              {item.away_team?.name}
                            </p>
                          </wrap>

                          <wrap className="NN">
                            <strong
                              style={
                                dark
                                  ? { color: "#dbdbdbff" }
                                  : { color: "#161819" }
                              }>
                              {item.score ? item.score.split("-")[1] : ""}
                            </strong>
                          </wrap>
                        </nav>
                      </span>
                    </div>
                  </Link>
                )}
              </>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default Search;
